import {Button} from 'antd'
import {docs_getSubCodiDocumentRows_Api, docs_reArrangeCodiDocumentRow_Api,} from 'apis'
import classNames from 'classnames'
import _ from 'lodash'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useEffect, useState} from 'react'
import {BsArrowsMove} from 'react-icons/bs'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import LoadingPage from 'views/LoadingPage'
import {getResponseItems} from 'views/Shared'
import DraggableWidget from 'views/Shared/components/DraggableWidget'
import {CodiContentTypes} from './codiHelpers'
import RowSettingContext, {SettingRowHeader} from './RowSettingContext'
import {NullAsDefault} from "../../Shared";

const renderDraggingItem = (item, {isDragging = false}) => {
    const {title, content, index_cd, index_typecd, short_index_name} =
    item ?? {}
    const is_content = index_typecd === CodiContentTypes.CONTENT
    return (
        <Translate>
            {(t) => (
                <div className="flex gap-3">
                    <BsArrowsMove className="text-color-400 mt-1"/>
                    <span
                        className={
                            is_content
                                ? 'flex flex-col flex-1'
                                : 'space-x-1 flex-1 leading-tight'
                        }>
            <span
                className={classNames(
                    'text-sm',
                    is_content
                        ? 'font-medium text-color-100 italic'
                        : 'font-semibold text-color-000'
                )}>
              {short_index_name
                  ? `${short_index_name}: `
                  : [CodiContentTypes.CLAUSE, CodiContentTypes.POINT].includes(
                      index_typecd
                  )
                      ? `${t(index_typecd)} ${index_cd}: `
                      : is_content
                          ? `${t(index_typecd)}: `
                          : `${index_cd}: `}
            </span>
            <span className="text-sm text-color-400 italic">
              {title ?? content ?? null}
            </span>
          </span>
                </div>
            )}
        </Translate>
    )
}

const ArrangeContent = ({
                            style,
                            className,
                            onCancel = NullAsDefault,
                            onSuccess = NullAsDefault,
                        }) => {
    const t = useTranslate()

    const [orderedList, setOrderList] = useState([])

    const [isSubmitting, setSubmitting] = useState(false)

    const {
        item,
        codi_id,
        row_id,
        isLoading = false,
    } = useContext(RowSettingContext)

    const {handleAsyncAction = NullAsDefault} = useAsyncAction({
        apiInfo: docs_reArrangeCodiDocumentRow_Api,
        query: bindQueryParams([{id: codi_id}, {row_id}]),
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            setSubmitting(false)
            onSuccess()
        },
        onError: (e, o) => {
            setSubmitting(false)
            notifyOnError(t)(e, o)
        },
    })

    const handleSubmit = useCallback(() => {
        if (!_.isEmpty(orderedList)) {
            setSubmitting(true)
            handleAsyncAction({codi_document_row_ids: orderedList.join(',')})
        }
    }, [orderedList])

    const onDrop = ({items, draggableId, destination: order_number}) => {
        setOrderList(items)
    }

    const {response, isLoading: fetchingSubRows = false} = useAsync({
        query: bindQueryParams([{id: codi_id}, {row_id}]),
        apiInfo: docs_getSubCodiDocumentRows_Api,
    })

    const data = Array.from(getResponseItems(response))

    useEffect(() => {
        const array = data.map(({id}) => id)
        if (!_.isEqual(array, orderedList)) {
            setOrderList(array)
        }
    }, [response])

    return (
        <div
            style={style}
            className={classNames(
                'flex flex-col flex-1 px-8 custom-form-codi',
                className
            )}>
            <div className="flex flex-col w-full gap-2 py-2 custom-form-codi">
                <div className="flex justify-center font-bold text-color-000 text-xl uppercase">
                    {t('arrange content')}
                </div>
                {Boolean(isLoading || fetchingSubRows) ? (
                    <LoadingPage/>
                ) : (
                    <div className="flex flex-col">
                        <SettingRowHeader/>
                        <DraggableWidget
                            key={orderedList}
                            sortBy="order_number"
                            onCompleted={onDrop}
                            renderItem={renderDraggingItem}
                            data={Array.from(orderedList ?? []).map((id) =>
                                _.find(data, {id})
                            )}
                            withClassNames={(isDragging, classNames) => [
                                ...classNames,
                                'rounded-lg mb-2',
                                isDragging
                                    ? 'border-2 border-blue-400'
                                    : 'border border-color-50 background',
                            ]}
                            itemClassName="w-full p-2 flex flex-col space-y-2"
                        />
                    </div>
                )}
            </div>
            <div className="flex-1"/>
            <div className="z-10 p-2 sticky bottom-0 background pointer-events-none">
                <div className="flex items-center gap-2 pointer-events-auto justify-end p-2">
                    <Button
                        onClick={onCancel}
                        disabled={!!isSubmitting}
                        className="button-rounded-full no-border font-medium">
                        {t('cancel')}
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={!!isSubmitting}
                        disabled={!!isSubmitting || _.isEmpty(orderedList)}
                        className="rounded-lg no-shadow no-text-shadow font-medium">
                        {t('save')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default ArrangeContent
